import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

// Get token from localStorage
const token = localStorage.getItem('token');

export const getFusion360CredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    // Find the Fusion360 master connector details
    const fusion360Connector = masterConnectors.find(mc => 
      (mc.mcRecId?.toString() === mcId?.toString() || 
       mc.mcId?.toString() === mcId?.toString()) && 
      mc.mcName === "Fusion 360"
    );
    if (!fusion360Connector) {
      throw new Error('Fusion360 master connector not found');
    }

    // Fetch client ID and client secret from the API endpoint using connector instance ID
    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });
    if (!paramResponse.ok) {
      throw new Error('Failed to fetch connector parameters');
    }
    const connectorParams = await paramResponse.json();
    
    const clientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;
    if (!clientId) {
      throw new Error('Client ID not found in connector parameters');
    }

    // Store the connector ID for after redirect
    localStorage.setItem('PendingFusion360ConnectorId', connectorId);
    localStorage.setItem('ConnectorType', 'Fusion360');

    // Use Master Connector auth URL
    const authEndpoint = fusion360Connector.mcAuthurl;
    if (!authEndpoint) {
      throw new Error('Fusion 360 Authentication URL is missing from Master Connector data');
    }
    const fusion360AuthUrl = `${authEndpoint}${authEndpoint.includes('?') ? '&' : '?'}client_id=${encodeURIComponent(clientId)}&scope=${encodeURIComponent('data:read data:write')}&redirect_uri=${encodeURIComponent(process.env.REACT_APP_OAUTH_REDIRECT_URI)}&response_type=code&state=${encodeURIComponent(connectorId)}`;
    
    // Check if we already have a stored authorization code
    const storedAuthCode = localStorage.getItem('AuthCode');

    if (storedAuthCode) {
      // If we have a stored authorization code, exchange it for an access token
      const tokenResponse = await exchangeFusion360AuthCodeForToken(
        storedAuthCode, 
        clientId, 
        mcId
      );

      // Clean up the stored authorization code
      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingFusion360ConnectorId');
      localStorage.removeItem('ConnectorType');

      // Return the access token
      return tokenResponse;
    } else {
      // Redirect to Fusion360 authentication
      window.location.href = fusion360AuthUrl;
      // Return null since we're redirecting
      return null;
    }
  } catch (error) {
    console.error('Error getting Fusion360 credentials:', error);
    throw error;
  }
};

// Helper function to exchange authorization code for Fusion360 token
export const exchangeFusion360AuthCodeForToken = async (authCode, clientId, mcId) => {
  try {
    const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/fusion360/fullTokens?mcId=${mcId}`;
    
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
      },
      body: new URLSearchParams({
        code: authCode,
        grant_type: 'authorization_code',
        redirect_uri: process.env.REACT_APP_OAUTH_REDIRECT_URI
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange error response:', errorText);
      throw new Error(`Failed to exchange authorization code for token: ${response.status}`);
    }

    const tokenData = await response.json();
    
    // Get the connector ID that was stored before redirect
    const connectorId = localStorage.getItem('PendingFusion360ConnectorId');
    
    if (connectorId) {
      // Update the connector with token data
      const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;
      
      try {
        // First, get the current connector data
        const connectorResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
        if (!connectorResponse.ok) {
          throw new Error(`Failed to fetch connector data: ${connectorResponse.status}`);
        }
        const connectorData = await connectorResponse.json();
        
        // Calculate token expiry dates
        const primaryExpiresIn = parseInt(tokenData.primary_token?.expires_in) || 3600;
        const secondaryExpiresIn = parseInt(tokenData.secondary_token?.expires_in) || 3600;
        
        // Calculate the 14-day expiry date for refresh token
        const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
        const refreshTokenExpiry = new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString();
        
        // Prepare updated connector data
        const updatedConnectorData = {
          ...connectorData,
          token: tokenData.primary_token?.access_token,
          refreshToken: tokenData.primary_token?.refresh_token,
          tokenExpiry: new Date(Date.now() + (primaryExpiresIn * 1000)).toISOString(),
          secondaryToken: tokenData.secondary_token?.access_token,
          secondaryTokenExpiry: new Date(Date.now() + (secondaryExpiresIn * 1000)).toISOString(),
          refreshUrl: connectorData.refreshUrl,
          refreshTokenExpiry: refreshTokenExpiry,
          status: 'Connected',
          connectedTime: new Date().toISOString()
        };
        
        // Make the PUT request to update the connector
        const updateResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedConnectorData)
        });
        
        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error('Update connector error:', errorText);
          throw new Error(`Failed to update connector with tokens: ${updateResponse.status}`);
        }
        
        console.log('Connector updated successfully with tokens');
        window.location.reload();
      } catch (error) {
        console.error('Error updating connector with tokens:', error);
      }
    }
    return tokenData;
  } catch (error) {
    console.error('Token exchange error:', error);
    throw error;
  }
};